import { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RoomScope } from "@/components/extranet/RatePlanDialog";
import { http, errMsg } from "@/lib/api";

const ROLE_DESC = { owner: "Semua akses + atur akun", manager: "Semua akses kecuali atur akun", staff: "Lihat semua, ubah reservasi & tarif saja" };
const EMPTY = { email: "", name: "", role: "staff", property_ids: [], active: true, password: "" };

export default function AccessPanel({ me, properties, t }) {
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState(null);
  const load = () => http.get("/extranet/staff").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      if (dlg.id) await http.put(`/extranet/staff/${dlg.id}`, dlg); else await http.post("/extranet/staff", dlg);
      toast.success("Akun disimpan"); setDlg(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => {
    if (!window.confirm(`Hapus akun ${s.email}?`)) return;
    try { await http.delete(`/extranet/staff/${s.id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const propName = (ids) => (ids?.length ? properties.filter((p) => ids.includes(p.id)).map((p) => p.name).join(", ") : t("Semua properti"));
  return (
    <div data-testid="access-panel">
      <div className="flex justify-end border-b border-[#E7E0D6] p-3">
        <button data-testid="access-add-button" onClick={() => setDlg({ ...EMPTY })} className="rounded-sm bg-[#B4693E] px-4 py-2 text-sm font-semibold text-white">{t("Tambah akun")}</button>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#FAF7F2] text-left text-xs uppercase text-stone-500"><tr><th className="px-5 py-2">{t("Nama")}</th><th>{t("Email")}</th><th>{t("Role")}</th><th>{t("Akses properti")}</th><th>Status</th><th /></tr></thead>
        <tbody className="divide-y divide-[#EFE9E0]">
          {list.map((s) => (
            <tr key={s.id} data-testid={`access-row-${s.id}`}>
              <td className="px-5 py-3 font-semibold">{s.name}</td><td>{s.email}</td>
              <td><span className="rounded bg-[#F3E7DA] px-2 py-0.5 text-xs font-semibold capitalize text-[#8A5A3B]">{s.role}</span></td>
              <td className="max-w-[260px] truncate text-xs">{propName(s.property_ids)}</td>
              <td className="text-xs">{s.active ? t("Aktif") : "Nonaktif"}</td>
              <td className="whitespace-nowrap pr-5 text-right">
                <button data-testid={`access-edit-${s.id}`} onClick={() => setDlg({ ...s, password: "" })} className="rounded p-2 hover:bg-stone-100"><Pencil className="h-4 w-4" /></button>
                {s.id !== me.id && <button data-testid={`access-delete-${s.id}`} onClick={() => del(s)} className="rounded p-2 text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="extranet" data-testid="access-dialog">
          <DialogHeader><DialogTitle>{dlg?.id ? "Edit akun" : t("Tambah akun")}</DialogTitle></DialogHeader>
          {dlg && <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>{t("Nama")}</Label><Input data-testid="access-name" value={dlg.name} onChange={(e) => setDlg({ ...dlg, name: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("Email")}</Label><Input data-testid="access-email" disabled={!!dlg.id} value={dlg.email} onChange={(e) => setDlg({ ...dlg, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{t("Role")}</Label>
              <Select value={dlg.role} onValueChange={(v) => setDlg({ ...dlg, role: v })}><SelectTrigger data-testid="access-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(ROLE_DESC).map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent></Select>
              <p className="mt-1 text-xs text-stone-500">{ROLE_DESC[dlg.role]}</p></div>
            <div><Label>{t("Kata sandi")} {dlg.id && "(kosongkan jika tidak diganti)"}</Label><Input data-testid="access-password" type="password" value={dlg.password} onChange={(e) => setDlg({ ...dlg, password: e.target.value })} className="mt-1.5" /></div>
            <div className="sm:col-span-2"><Label>{t("Akses properti")}</Label><RoomScope rooms={properties} value={dlg.property_ids} onChange={(v) => setDlg({ ...dlg, property_ids: v })} testid="access-properties" /></div>
            <label className="flex items-center gap-3 text-sm"><Switch data-testid="access-active" checked={dlg.active} onCheckedChange={(v) => setDlg({ ...dlg, active: v })} /> {t("Aktif")}</label>
          </div>}
          <DialogFooter><button data-testid="access-save" onClick={save} className="rounded-sm bg-[#B4693E] px-5 py-2 font-semibold text-white">{t("Simpan")}</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
